import { useHistory } from 'react-router-dom'
import { IntegrationAccount } from '@phrasee/phrasee-typings/typings/integrations/accounts'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cond from 'lodash/cond'
import stubTrue from 'lodash/stubTrue'

import Button from 'common/components/button'
import { Variant } from 'common/components/button/interfaces'
import { capitalizeFirstLetter } from 'common/helpers/string'
import { useAppSelector } from 'common/hooks/redux'
import { ArrowDropdownDown } from 'common/icons'

import useUpdateIntegrationAccountMutation from '../api/mutations/useUpdateIntegrationAccountMutation'

import IntegrationsMenu from './IntegrationsMenu'
import { Options } from './interfaces'

type Props = {
  accounts: IntegrationAccount[]
  children: React.ReactNode
  contentId: string
  elementId: number
  variant?: Variant
  className?: string
  placement?: 'bottomLeft' | 'bottomCenter' | 'bottomRight'
}

const placementToAlign = (
  placement: 'bottomLeft' | 'bottomCenter' | 'bottomRight'
): 'start' | 'center' | 'end' => {
  if (placement === 'bottomLeft') {
    return 'start'
  }
  if (placement === 'bottomRight') {
    return 'end'
  }
  return 'center'
}

const IntegrationsSelect = ({
  accounts,
  variant,
  contentId,
  elementId,
  children,
  className,
  placement = 'bottomCenter',
}: Props) => {
  const history = useHistory()

  const accountId = useAppSelector((state) => state.authStates.accountId)

  const updateIntegrationAccountMutation = useUpdateIntegrationAccountMutation()

  const submitOptions: Options[] = accounts.map((account) => ({
    key: account.id,
    logoUrl: account.logoUrl,
    provider: account.provider,
    label: `${capitalizeFirstLetter(account.provider)} ${account.label ?? ''}`,
  }))

  const handleItemAction = cond([
    [
      (id: string) => id === 'redirect',
      () => history.push(`/admin/accounts/${accountId}/integrations`),
    ],
    [
      stubTrue,
      (id: string) =>
        updateIntegrationAccountMutation.mutate({
          accountId,
          contentId,
          elementId,
          updatedElement: { integration_account_id: id },
        }),
    ],
  ])

  return (
    <div id="integrations-dropdown" className={className}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            variant={variant}
            suffixIcon={<ArrowDropdownDown isDefaultColor={false} />}
            className="updated-component"
          >
            {children}
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align={placementToAlign(placement)}
            sideOffset={4}
          >
            <IntegrationsMenu
              options={submitOptions}
              onClick={(id) => handleItemAction(id)}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  )
}

export default IntegrationsSelect
